import { DataBlockVariant, useDataBlockType } from '@lmt/shared-types';
import {
  DataBlockUiEditor,
  DataBlockUiEditorProps,
} from '@lmt/unicorn-shared/app/components/measurements/DataBlockUiEditor';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { useUniSelector } from '@lmt/unicorn-shared/app/store/types';
import clsx from 'clsx';
import React, { FC, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonColor } from '../common/ui-kit/buttons/Button';
import { FormComponentProps } from '../global-forms/GlobalForms.types';
import { caseInformationDataBlockTypeName } from './common';

import styles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';
import localStyles from './DataBlockView.module.scss';

export type DataBlockViewProps = FormComponentProps & {
  dataBlockId: number;
  dataBlockTypeId: number;
  patientId: number;
};

export const DataBlockView: FC<DataBlockViewProps> = (props) => {
  const {
    formType,
    formId,
    tryCompleteForm,
    dataBlockId,
    dataBlockTypeId,
    loadBlocking,
  } = props;

  const i18next = useTranslation();
  const claims = useClaims();
  const selectedDataBlockType = useDataBlockType(dataBlockTypeId);

  const actions: DataBlockUiEditorProps['actions'] = useRef(null);

  const onDelete = useCallback(async () => {
    await loadBlocking(
      formId,
      i18next.t('data_blocks.loading_delete'),
      async () => {
        await tryCompleteForm(formType, formId, async () => {
          await actions.current?.delete();
        }).catch((e) => console.error('DataBlockView.onDelete', e));
      },
    );
  }, [formType, formId, tryCompleteForm, actions.current]);

  const onSave = useCallback(async () => {
    await loadBlocking(
      formId,
      i18next.t('data_blocks.loading_save'),
      async () => {
        await tryCompleteForm(formType, formId, async () => {
          await actions.current?.save();
        }).catch((e) => console.error('DataBlockView.onSave', e));
      },
    );
  }, [formType, formId, tryCompleteForm, actions.current]);
  return (
    <div
      className={clsx(styles.autoSizedContainer, localStyles.container)}
      data-test-id="dataBlockViewPopup"
    >
      <div className={styles.dataContainer}>
        <DataBlockUiEditor
          dataBlockTypeId={props.dataBlockTypeId}
          dataBlockId={dataBlockId}
          patientId={props.patientId}
          type={
            selectedDataBlockType?.variant === DataBlockVariant.Patient
              ? 'update_patient_block'
              : 'update_data_block'
          }
          isEditable={claims.permissions.dataBlockManagement}
          actions={actions}
        />
      </div>
      {claims.permissions.dataBlockManagement && (
        <div className={styles.submitContainer}>
          {(dataBlockId ||
            selectedDataBlockType?.variant === DataBlockVariant.Patient) && (
            <>
              {selectedDataBlockType?.variant !== DataBlockVariant.Patient && (
                <Button
                  color={ButtonColor.Danger}
                  disabled={
                    selectedDataBlockType?.variant === DataBlockVariant.Case &&
                    selectedDataBlockType?.value ===
                      caseInformationDataBlockTypeName
                  }
                  onClick={onDelete}
                  title={i18next.t('data_blocks.delete_button')}
                  className={styles.submit}
                />
              )}

              <Button
                onClick={onSave}
                title={i18next.t('data_blocks.save_button')}
                className={styles.submit}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DataBlockView;
